import { z } from 'zod';
/**
 * Native (mobile) sign-in contract — apps/api `modules/auth`
 * (mobile-auth.controller.ts, oauth/oauth.controller.ts).
 *
 * The native client never holds a NextAuth cookie: every successful sign-in
 * answers an access/refresh pair from AuthTokenService (access 15m, refresh 7d,
 * rotated on POST /api/auth/refresh) and the client sends
 * `Authorization: Bearer <accessToken>` from then on.
 *
 * Error bodies are `{ error: <code> }` with the codes in
 * {@link MobileAuthErrorCode}; a 429 also carries `retryAfterSec`.
 */
/** Same rules as the web sign-up form (apps/auction src/shared/utils/zod.ts). */
export const MobilePasswordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(128, 'Password must not exceed 128 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');
/** `POST /api/auth/login`. Sign-in keeps the relaxed web rule: any non-empty password. */
export const LoginRequestSchema = z.object({
    email: z.string().trim().email().max(254),
    password: z.string().min(1).max(128),
});
/**
 * `POST /api/auth/register` — the web sign-up form's fields. Answers 202
 * `{ accepted: true }` with NO tokens: sign-in is possible only after the
 * mailed confirmation link is opened (same rule as the web, 2026-09-26).
 */
export const RegisterRequestSchema = z
    .object({
    email: z.string().trim().email().max(254),
    password: MobilePasswordSchema,
    confirmPassword: z.string(),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    displayName: z.string().trim().min(1).max(100),
})
    .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
        ctx.addIssue({
            path: ['confirmPassword'],
            code: z.ZodIssueCode.custom,
            message: 'Passwords do not match.',
        });
    }
});
export const RegisterAcceptedSchema = z.object({ accepted: z.literal(true) });
export const OAuthProviderSchema = z.enum(['google', 'apple', 'facebook']);
/**
 * One provider identity linked to a `users` row (`users.oauthIdentities[]`,
 * additive and optional). Written only by apps/api's native OAuth sign-in —
 * the web's NextAuth adapter keeps its own `accounts` collection.
 * `providerUserId` is the provider's stable subject (`sub` / Facebook id).
 */
export const OAuthIdentitySchema = z.object({
    provider: OAuthProviderSchema,
    providerUserId: z.string().min(1),
    linkedAt: z.coerce.date(),
});
/** Google / Apple hand the app an OpenID Connect ID token. */
export const OAuthIdTokenRequestSchema = z.object({
    idToken: z.string().min(1).max(8192),
});
/**
 * Native Sign in with Apple body. `nonce` is the RAW nonce the app generated;
 * the app passes `sha256(nonce)` (hex) to `AppleAuthentication.signInAsync`
 * (Expo convention), Apple embeds that hash in the identity token, and the
 * server checks `token.nonce === sha256(raw)` — a replayed or intercepted
 * token without its raw nonce is refused. REQUIRED: a body without it is
 * refused with 400 VALIDATION_FAILED before any token is looked at (the
 * mobile client sends it since plan phase 7).
 */
export const OAuthAppleRequestSchema = OAuthIdTokenRequestSchema.extend({
    nonce: z.string().min(16).max(256),
});
/** Facebook Login hands the app a user access token. */
export const OAuthAccessTokenRequestSchema = z.object({
    accessToken: z.string().min(1).max(8192),
});
/** `POST /api/auth/oauth/:provider` body — `idToken` for google/apple, `accessToken` for facebook. */
export const OAuthTokenRequestSchema = z.union([
    OAuthIdTokenRequestSchema,
    OAuthAccessTokenRequestSchema,
]);
/** The provider-specific body for one provider. */
export function oauthTokenRequestSchemaFor(provider) {
    if (provider === 'facebook')
        return OAuthAccessTokenRequestSchema;
    return provider === 'apple' ? OAuthAppleRequestSchema : OAuthIdTokenRequestSchema;
}
/** `AuthTokenService.IssuedTokens` on the wire. */
export const IssuedTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    tokenType: z.literal('Bearer'),
    expiresIn: z.string(),
    refreshExpiresIn: z.string(),
});
export const MobileAuthUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: z.array(z.string()),
});
/** 200 body of login and of every OAuth sign-in. */
export const IssuedTokensResponseSchema = IssuedTokensSchema.extend({
    user: MobileAuthUserSchema,
});
/** `POST /api/auth/refresh` and `POST /api/auth/logout` body. */
export const RefreshTokenRequestSchema = z.object({
    refreshToken: z.string().min(1).max(4096),
});
/** `GET /api/mobile/config` — anonymous remote config read at app start. */
export const MobileConfigSchema = z.object({
    providers: z.object({
        password: z.boolean(),
        google: z.boolean(),
        apple: z.boolean(),
        facebook: z.boolean(),
    }),
    /** Semver; an older app shows a blocking "update" screen. */
    minAppVersion: z.string(),
    /** Version of this mobile API surface (bumped on a breaking change). */
    apiVersion: z.string(),
    /**
     * Where a tester or user reaches a human (the Telegram support chat link,
     * env `MOBILE_SUPPORT_URL`). Additive and optional: absent = the app hides
     * its «Write in Telegram» button; an older app ignores the field.
     */
    supportUrl: z.string().url().optional(),
});
export const MOBILE_API_VERSION = '1';
export const MobileAuthErrorCodeSchema = z.enum([
    'VALIDATION_FAILED',
    'INVALID_CREDENTIALS',
    'EMAIL_NOT_VERIFIED',
    'TOO_MANY_ATTEMPTS',
    'INVALID_PROVIDER_TOKEN',
    'EMAIL_REQUIRED',
    'EMAIL_NOT_VERIFIED_BY_PROVIDER',
    'PROVIDER_DISABLED',
    'PROVIDER_NOT_CONFIGURED',
    'PROVIDER_UNAVAILABLE',
    'UNKNOWN_PROVIDER',
    /** Facebook is never linked by address: sign in another way, then link from settings. */
    'ACCOUNT_EXISTS_LINK_FROM_SETTINGS',
    /** Link from settings: this provider identity already opens a DIFFERENT account. */
    'IDENTITY_LINKED_TO_ANOTHER_ACCOUNT',
    /** Link from settings: the caller already has another identity of this provider linked. */
    'PROVIDER_ALREADY_LINKED',
    /**
     * Link from settings: the account carried an identity attached before its
     * address was proven. It was removed and every session revoked — sign in
     * again (password / Google / Apple), then link.
     */
    'SIGN_IN_AGAIN_TO_LINK',
]);
/** Facebook data-deletion callback answer (developers.facebook.com data deletion callback). */
export const FacebookDataDeletionResponseSchema = z.object({
    url: z.string(),
    confirmation_code: z.string(),
});
/**
 * `POST /api/mobile/auth/link/facebook` body — the same user access token the
 * Facebook sign-in sends (verified server-side by the same Graph check).
 */
export const LinkFacebookRequestSchema = OAuthAccessTokenRequestSchema;
/** One identity that can open the account (stale identities are never listed). */
export const LinkedIdentitySchema = z.object({
    provider: OAuthProviderSchema,
    linkedAt: z.coerce.date(),
});
/**
 * `GET /api/mobile/auth/linked-accounts` (and the 200 body of a link): the
 * caller's sign-in methods as the API knows them. `hasPassword` says whether a
 * local password exists (never the hash). A row with `hasPassword: false` and
 * only `facebook` identities is a Facebook-only account: proving its address
 * (confirmation mail) would make that identity stale, so the app must not push
 * it into the email-confirmation flow.
 */
export const LinkedAccountsSchema = z.object({
    hasPassword: z.boolean(),
    identities: z.array(LinkedIdentitySchema),
});
//# sourceMappingURL=mobile-auth.js.map